<script setup>
import { computed, onMounted, ref } from "vue";
import Sidebar from "../components/Sidebar.vue";
import TopBar from "../components/TopBar.vue";
import ChatAssistant from "../components/ChatAssistant.vue";
import { listSales } from "../db/sales.js";
import { formatMoney } from "../utils/analyticsUtils.js";

const sidebarCollapsed = ref(false);
const loading = ref(false);
const error = ref("");
const rows = ref([]);

const start = ref("");
const end = ref("");
const q = ref("");
const periodo = ref("30d");

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value;
}

function toISODate(d) {
  return new Date(d).toISOString().slice(0, 10);
}

function clampPercent(v) {
  const n = Math.abs(Number(v || 0));
  if (!isFinite(n)) return 0;
  return Math.min(100, n);
}

function setDefaultRange() {
  setPeriodo("30d");
}

function setPeriodo(p) {
  periodo.value = p;
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const endD = new Date(now);
  endD.setHours(23, 59, 59, 999);

  if (p === "hoy") {
    start.value = toISODate(today);
    end.value = toISODate(endD);
    return;
  }
  if (p === "7d") {
    const d1 = new Date(endD);
    d1.setDate(d1.getDate() - 6);
    start.value = toISODate(d1);
    end.value = toISODate(endD);
    return;
  }
  if (p === "mes") {
    const d1 = new Date(endD.getFullYear(), endD.getMonth(), 1);
    start.value = toISODate(d1);
    end.value = toISODate(endD);
    return;
  }
  const d1 = new Date(endD);
  d1.setDate(d1.getDate() - 29);
  start.value = toISODate(d1);
  end.value = toISODate(endD);
}

function daysInRange() {
  if (!start.value || !end.value) return 0;
  const s = new Date(start.value + "T00:00:00");
  const e = new Date(end.value + "T00:00:00");
  const ms = e.getTime() - s.getTime();
  return Math.max(1, Math.round(ms / 86400000) + 1);
}

const filtered = computed(() => {
  const s = start.value ? new Date(start.value + "T00:00:00") : null;
  const e = end.value ? new Date(end.value + "T23:59:59") : null;
  const needle = q.value.trim().toLowerCase();
  return (rows.value || []).filter((r) => {
    const dt = r.created_at ? new Date(r.created_at) : null;
    if (s && dt && dt < s) return false;
    if (e && dt && dt > e) return false;
    if (!needle) return true;
    const hay = `${r.doc_number || ""} ${r.customer_name || ""} ${
      r.payment_method || ""
    }`.toLowerCase();
    return hay.includes(needle);
  });
});

const total = computed(() =>
  filtered.value.reduce((sum, r) => sum + Number(r.total || 0), 0)
);

const totalPrev = computed(() => {
  if (!start.value || !end.value) return 0;
  const len = daysInRange();
  const s0 = new Date(start.value + "T00:00:00");
  const prevEnd = new Date(s0.getTime() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (len - 1));
  const needle = q.value.trim().toLowerCase();
  return (rows.value || []).reduce((sum, r) => {
    const dt = r.created_at ? new Date(r.created_at) : null;
    if (!dt) return sum;
    if (dt < prevStart || dt > prevEnd) return sum;
    if (needle) {
      const hay = `${r.doc_number || ""} ${r.customer_name || ""} ${
        r.payment_method || ""
      }`.toLowerCase();
      if (!hay.includes(needle)) return sum;
    }
    return sum + Number(r.total || 0);
  }, 0);
});

const varPct = computed(() => {
  const prev = Number(totalPrev.value || 0);
  if (prev <= 0) return 0;
  return ((Number(total.value || 0) - prev) / prev) * 100;
});

const pctShown = computed(() => {
  const cur = Number(total.value || 0);
  if (cur <= 0) return 0;
  return clampPercent(varPct.value);
});

async function load() {
  loading.value = true;
  error.value = "";
  try {
    rows.value = await listSales();
  } catch (e) {
    error.value = (e && e.message) || String(e);
    rows.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  setDefaultRange();
  await load();
});
</script>

<template>
  <div class="layout">
    <Sidebar :collapsed="sidebarCollapsed" />
    <div class="main-content">
      <TopBar title="Reporte Ventas" @toggle-sidebar="toggleSidebar" />

      <div class="dashboard-container">
        <header class="hero-balance">
          <div class="hero-left">
            <span class="hero-label">Total ventas</span>
            <div class="hero-value">{{ formatMoney(total) }}</div>
            <div class="hero-var" :class="pctShown > 0 ? 'up' : 'down'">
              {{ pctShown.toFixed(1) }}%
            </div>
          </div>
          <div class="head-tools">
            <button
              class="chip"
              :class="{ active: periodo === 'hoy' }"
              @click="setPeriodo('hoy')"
            >
              Hoy
            </button>
            <button
              class="chip"
              :class="{ active: periodo === '7d' }"
              @click="setPeriodo('7d')"
            >
              7 días
            </button>
            <button
              class="chip"
              :class="{ active: periodo === '30d' }"
              @click="setPeriodo('30d')"
            >
              30 días
            </button>
            <button
              class="chip"
              :class="{ active: periodo === 'mes' }"
              @click="setPeriodo('mes')"
            >
              Este mes
            </button>
          </div>
        </header>

        <section class="kpis-cards">
          <div class="soft-card orange">
            <div class="card-title">Registros</div>
            <div class="card-value">{{ filtered.length }}</div>
            <div class="card-sub">Dentro del rango</div>
          </div>
          <div class="soft-card green">
            <div class="card-title">Promedio diario</div>
            <div class="card-value">
              {{
                formatMoney(
                  daysInRange() ? Number(total || 0) / daysInRange() : 0
                )
              }}
            </div>
            <div class="card-sub">{{ daysInRange() }} días</div>
          </div>
          <div class="soft-card purple">
            <div class="card-title">Búsqueda</div>
            <div class="card-value">{{ q ? "Activa" : "—" }}</div>
            <div class="card-sub">{{ q || "Sin filtro" }}</div>
          </div>
        </section>

        <section class="panel filters-panel">
          <div class="panel-head">
            <div>
              <b>Filtros</b>
              <div class="panel-sub">Ajusta rango y búsqueda</div>
            </div>
            <button class="chip" @click="load" :disabled="loading">
              <span class="material-symbols-rounded">refresh</span>
              Actualizar
            </button>
          </div>
          <div class="filters-grid">
            <label class="field">
              <span class="label">Desde</span>
              <div class="control">
                <span class="material-symbols-rounded icon"
                  >calendar_month</span
                >
                <input class="input" type="date" v-model="start" />
              </div>
            </label>
            <label class="field">
              <span class="label">Hasta</span>
              <div class="control">
                <span class="material-symbols-rounded icon"
                  >calendar_month</span
                >
                <input class="input" type="date" v-model="end" />
              </div>
            </label>
            <label class="field grow">
              <span class="label">Buscar</span>
              <div class="control">
                <span class="material-symbols-rounded icon">search</span>
                <input
                  class="input"
                  v-model="q"
                  placeholder="Folio, cliente, método..."
                />
              </div>
            </label>
          </div>
        </section>

        <section class="panel">
          <div class="panel-head">
            <div>
              <b>Ventas</b>
              <div class="panel-sub">{{ start }} → {{ end }}</div>
            </div>
          </div>

          <div v-if="error" class="alert">{{ error }}</div>
          <div v-if="loading" class="muted">Cargando…</div>

          <div v-else class="table-container">
            <table class="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Folio</th>
                  <th>Cliente</th>
                  <th>Método</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in filtered" :key="r.id" class="table-row">
                  <td class="date-text">
                    {{ new Date(r.created_at).toLocaleString() }}
                  </td>
                  <td class="text">{{ r.doc_number || "-" }}</td>
                  <td class="text">{{ r.customer_name || "Sin cliente" }}</td>
                  <td class="muted">{{ r.payment_method || "-" }}</td>
                  <td class="text-right total-text">
                    {{ formatMoney(r.total) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>

    <ChatAssistant />
  </div>
</template>

<style scoped>
.layout {
  display: flex;
  height: 100vh;
  background: var(--bg);
  color-scheme: light dark;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dashboard-container {
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
  flex: 1;
  overflow: auto;
  padding: 1.5rem;
  display: grid;
  gap: 1.25rem;
}

.hero-balance {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-radius: 16px;
  background: linear-gradient(135deg, #6a5cf5, #00c2ff);
  color: #fff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}

.hero-label {
  font-weight: 600;
  opacity: 0.9;
}

.hero-value {
  font-size: 2rem;
  font-weight: 900;
}

.hero-var {
  font-weight: 700;
  margin-top: 0.15rem;
}

.hero-var.up {
  color: #9ef0a1;
}

.hero-var.down {
  color: #fecaca;
}

.head-tools {
  display: inline-flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  padding: 0.4rem 0.6rem;
  cursor: pointer;
  color: var(--text);
}

.chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chip.active {
  background: var(--accent-gradient);
  color: white;
  border-color: transparent;
}

.kpis-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.soft-card {
  border-radius: 16px;
  padding: 0.95rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  position: relative;
  color: #fff;
}

.soft-card.orange {
  background: linear-gradient(135deg, #f97316, #fb7185);
}

.soft-card.green {
  background: linear-gradient(135deg, #10b981, #22c55e);
}

.soft-card.purple {
  background: linear-gradient(135deg, #6366f1, #a855f7);
}

.card-title {
  font-weight: 700;
  opacity: 0.95;
}

.card-value {
  font-size: 1.6rem;
  font-weight: 900;
  margin-top: 0.2rem;
}

.card-sub {
  font-size: 0.85rem;
  opacity: 0.9;
  margin-top: 0.15rem;
}

.panel {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.panel-sub {
  color: var(--accent);
  font-weight: 700;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.filters-panel {
  padding: 1rem 1.25rem 1.15rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
}

.field {
  display: grid;
  gap: 0.4rem;
}

.field.grow {
  grid-column: span 1;
}

.label {
  color: var(--muted);
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.02em;
}

.control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--border);
  background: rgba(15, 23, 42, 0.18);
  border-radius: 12px;
  padding: 0.55rem 0.65rem;
}

.icon {
  color: var(--accent);
  font-size: 18px;
}

.input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text);
  font-size: 0.9rem;
}

.alert {
  border: 1px solid rgba(239, 68, 68, 0.35);
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
  padding: 0.75rem;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.muted {
  color: var(--muted);
  padding: 0.5rem 0;
}

.table-container {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid var(--border);
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: 760px;
}

.table th,
.table td {
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid var(--border);
  text-align: left;
}

.table th {
  background: linear-gradient(
    135deg,
    rgba(198, 146, 61, 0.08) 0%,
    rgba(0, 194, 168, 0.06) 100%
  );
  color: var(--text);
  font-weight: 800;
  font-size: 0.85rem;
}

.table-row:hover {
  background: rgba(198, 146, 61, 0.06);
}

.text {
  color: var(--text);
  font-weight: 600;
}

.date-text {
  color: var(--muted);
  font-size: 0.85rem;
}

.total-text {
  color: var(--text);
  font-weight: 900;
}

.text-right {
  text-align: right;
}

@media (max-width: 900px) {
  .kpis-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  .filters-grid {
    grid-template-columns: 1fr 1fr;
  }
  .field.grow {
    grid-column: span 2;
  }
}

@media (max-width: 520px) {
  .hero-balance {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }
  .hero-value {
    font-size: 1.75rem;
  }
  .kpis-cards {
    grid-template-columns: 1fr;
  }
  .filters-grid {
    grid-template-columns: 1fr;
  }
  .field.grow {
    grid-column: span 1;
  }
}
</style>
